import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { fetchAuditRecordingPlaybackInfo, isRecordingEventPlayable } from '../utils/api';
import { RecordingPlaybackModal } from './RecordingPlaybackModal';

const RECORDING_CLICK_GUARD_UNTIL_KEY = '__recordingClickGuardUntil';
const RECORDING_CLICK_GUARD_WINDOW_MS = 350;
const PLAYBACK_AVAIL_TTL_MS = 60 * 1000;
const playbackAvailabilityCache = new Map();
const playbackAvailabilityPending = new Map();

const markRecordingClickGuard = () => {
  if (typeof window === 'undefined') return;
  window[RECORDING_CLICK_GUARD_UNTIL_KEY] = Date.now() + RECORDING_CLICK_GUARD_WINDOW_MS;
};

export const isAuditRecordingReady = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  const v = entry.recording_ready;
  return v === true || v === 1;
};

const PLAYBACK_RETRY_TOOLTIP = "Couldn't open the recording. Try again.";
const PLAYBACK_NOT_READY_TOOLTIP = "Recording isn't ready to play yet.";

const isRetryablePlaybackError = (err) => {
  const status = Number(err?.status);
  const msg = String(err?.message || '');
  if (/transcoding in progress|recording file missing/i.test(msg)) return false;
  if (status >= 500 || status === 408 || status === 429) return true;
  if (status >= 400 && status < 500) return false;
  return /timeout|network|failed to fetch|load failed/i.test(msg) || !status;
};

const normalizePlaybackPayload = (data) => {
  const rec = data?.recording;
  // Event play opens when HLS chunks exist. processing (ffmpeg running) does not.
  if (!rec?.id || !isRecordingEventPlayable(rec)) {
    return { available: false, payload: null, reason: 'not_ready' };
  }
  const rawSeek = Number(data?.seek_seconds);
  const seek = Number.isFinite(rawSeek) ? Math.max(0, rawSeek) : 0;
  return {
    available: true,
    payload: { recording: rec, seekSeconds: seek },
    reason: null,
  };
};

const getFreshCachedPlaybackAvailability = (auditId) => {
  if (!auditId) return null;
  const cached = playbackAvailabilityCache.get(auditId);
  if (!cached) return null;
  if (Date.now() - cached.checkedAt > PLAYBACK_AVAIL_TTL_MS) return null;
  return cached;
};

const resolvePlaybackAvailability = async (auditId, { force = false } = {}) => {
  if (!auditId) return { available: false, payload: null, checkedAt: Date.now(), reason: 'not_ready' };
  if (!force) {
    const fresh = getFreshCachedPlaybackAvailability(auditId);
    if (fresh) return fresh;
    const pending = playbackAvailabilityPending.get(auditId);
    if (pending) return pending;
  }

  const pendingPromise = (async () => {
    try {
      const data = await fetchAuditRecordingPlaybackInfo(auditId);
      const parsed = normalizePlaybackPayload(data);
      const result = { ...parsed, checkedAt: Date.now() };
      playbackAvailabilityCache.set(auditId, result);
      return result;
    } catch (err) {
      const reason = isRetryablePlaybackError(err) ? 'retryable' : 'not_ready';
      const result = { available: false, payload: null, checkedAt: Date.now(), reason, error: err };
      if (reason !== 'retryable') playbackAvailabilityCache.set(auditId, result);
      return result;
    } finally {
      playbackAvailabilityPending.delete(auditId);
    }
  })();

  playbackAvailabilityPending.set(auditId, pendingPromise);
  return pendingPromise;
};

export const AuditRecordingPlayButton = ({
  log = null,
  enabled = true,
  playTooltip = 'Play recording from this event',
  placement = 'bottom',
  buttonSx = {},
  buttonSize = 32,
  iconSize = 22,
  anchorSx = {},
}) => {
  const [auditPlaybackLoading, setAuditPlaybackLoading] = useState(false);
  const [auditPlaybackOpen, setAuditPlaybackOpen] = useState(false);
  const [auditPlaybackRecording, setAuditPlaybackRecording] = useState(null);
  const [auditPlaybackSeek, setAuditPlaybackSeek] = useState(null);
  const [auditPlaybackSession, setAuditPlaybackSession] = useState(0);
  const [playError, setPlayError] = useState('');
  const [hoverTooltipOpen, setHoverTooltipOpen] = useState(false);
  const availabilityPayloadRef= useRef(null);   

  const hasRecordingReady = Boolean(log?.audit_id) && isAuditRecordingReady(log);
  const auditId = log?.audit_id || null;
  const canAttemptPlayback = enabled && hasRecordingReady && Boolean(auditId);
  const anchored = anchorSx && Object.keys(anchorSx).length > 0;

  useEffect(() => {
    setPlayError('');
    availabilityPayloadRef.current = null;
  }, [auditId, hasRecordingReady]);

  if (!canAttemptPlayback) return null;

  // Match corner controls (e.g. check-out on audit tiles): same inset from top and side via 32×32 anchor + overflow for scale(1.1).
  const handleCloseAuditPlayback = () => {
    markRecordingClickGuard();
    setHoverTooltipOpen(false);
    setAuditPlaybackOpen(false);
    setAuditPlaybackRecording(null);
    setAuditPlaybackSeek(null);
  };

  const handlePlaybackUnavailable = () => {
    availabilityPayloadRef.current = null;
    playbackAvailabilityCache.delete(auditId);
    setPlayError(PLAYBACK_NOT_READY_TOOLTIP);
  };

  const handlePlayAuditRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    markRecordingClickGuard();
    setHoverTooltipOpen(false);
    if (auditPlaybackLoading) return;
    if (!auditId) return;
    setPlayError('');
    setAuditPlaybackLoading(true);
    try {
      let payload = availabilityPayloadRef.current;
      if (!payload) {
        const cached = getFreshCachedPlaybackAvailability(auditId);
        if (cached?.available && cached.payload) {
          payload = cached.payload;
        } else {
          const resolved = await resolvePlaybackAvailability(auditId, {
            force: Boolean(cached && !cached.available),
          });
          if (!resolved.available || !resolved.payload) {
            setPlayError(
              resolved.reason === 'retryable' ? PLAYBACK_RETRY_TOOLTIP : PLAYBACK_NOT_READY_TOOLTIP
            );
            return;
          }
          payload = resolved.payload;
        }
        availabilityPayloadRef.current = payload;
      }

      const rec = payload?.recording;
      if (!rec?.id || !isRecordingEventPlayable(rec)) {
        availabilityPayloadRef.current = null;
        playbackAvailabilityCache.delete(auditId);
        setPlayError(PLAYBACK_NOT_READY_TOOLTIP);
        return;
      }

      setAuditPlaybackRecording(rec);
      const seek = Number(payload?.seekSeconds);
      setAuditPlaybackSeek(Number.isFinite(seek) && seek >= 0 ? seek : 0);
      setAuditPlaybackSession((n) => n + 1);
      setAuditPlaybackOpen(true);
    } catch (err) {
      availabilityPayloadRef.current = null;
      if (isRetryablePlaybackError(err)) setPlayError(PLAYBACK_RETRY_TOOLTIP);
    } finally {
      setAuditPlaybackLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        title={auditPlaybackLoading ? 'Opening recording…' : (playError || playTooltip)}
        arrow
        placement={placement}
        open={auditPlaybackOpen || auditPlaybackLoading ? false : hoverTooltipOpen}
        onOpen={() => {
          if (!auditPlaybackOpen && !auditPlaybackLoading) setHoverTooltipOpen(true);
        }}
        onClose={() => setHoverTooltipOpen(false)}
        disableHoverListener={auditPlaybackOpen || auditPlaybackLoading}
        disableFocusListener={auditPlaybackOpen || auditPlaybackLoading}
        disableTouchListener={auditPlaybackOpen || auditPlaybackLoading}
      >
        <Box
          data-recording-play-trigger="true"
          onMouseLeave={() => setHoverTooltipOpen(false)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={{
            position: anchored ? 'absolute' : 'relative',
            zIndex: 20,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: buttonSize,
            height: buttonSize,
            overflow: 'visible',
            ...anchorSx,
          }}
        >
          <IconButton
            data-recording-play-trigger="true"
            size="small"
            aria-busy={auditPlaybackLoading}
            onClick={handlePlayAuditRecording}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              markRecordingClickGuard();
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              markRecordingClickGuard();
            }}
            sx={{
              bgcolor: 'rgba(229, 229, 229, 0.95)',
              color: '#0f172a',
              width: buttonSize,
              height: buttonSize,
              transform: 'scale(1.1)',
              borderRadius: 4,
              '&:hover': {
                bgcolor: 'rgba(229, 229, 229, 0.95)',
                transform: 'scale(1.1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
              ...buttonSx,
            }}
          >
            {auditPlaybackLoading ? (
              <CircularProgress size={Math.max(14, iconSize - 6)} thickness={5} sx={{ color: '#0f172a' }} />
            ) : (
              <PlayArrow sx={{ fontSize: iconSize }} />
            )}
          </IconButton>
        </Box>
      </Tooltip>

      <RecordingPlaybackModal
        open={auditPlaybackOpen}
        recording={auditPlaybackRecording}
        onClose={handleCloseAuditPlayback}
        onPlaybackUnavailable={handlePlaybackUnavailable}
        initialSeekSeconds={auditPlaybackSeek}
        playbackSessionId={auditPlaybackSession}
      />
    </>
  );
};

export default AuditRecordingPlayButton;
